import { clientSDK } from "@istari-frontend/api-client";
import type { TrackedFile } from "@istari/istari-client";
import { useQuery, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";

import { systemsQueryKeys } from "./useSystems";

export type ConfigurationTrackedFile = Pick<
  TrackedFile,
  "id" | "fileId" | "currentFileRevisionId" | "pinnedFileRevisionId" | "archiveStatus" | "resourceId"
> & {
  specifierType: string;
  /** System-rooted tracked-file path (`<sysHex>.<labels…>.<fileIdHex>`); carries folder placement. */
  path?: string;
};

/**
 * Fetches all tracked files for a configuration with parallel pagination.
 * Returns metadata about tracked files including fileId (model/artifact ID) and revision IDs.
 * This data is accessible regardless of whether the user has access to the actual file content.
 *
 * @todo This should be refactored to useInfiniteQuery and virtualized in the future
 */
export const useListConfigurationTrackedFiles = (
  configurationId: string | undefined,
  options?: Omit<UseQueryOptions<ConfigurationTrackedFile[], Error>, "queryKey" | "queryFn">,
): UseQueryResult<ConfigurationTrackedFile[], Error> =>
  useQuery<ConfigurationTrackedFile[], Error>({
    queryKey: systemsQueryKeys.configurationTrackedFiles(configurationId!),
    queryFn: () => (configurationId ? fetchConfigurationTrackedFiles(configurationId) : Promise.resolve([])),
    enabled: !!configurationId,
    staleTime: Infinity,
    ...options,
  });

export async function fetchConfigurationTrackedFiles(configurationId: string): Promise<ConfigurationTrackedFile[]> {
  const firstResponse = await clientSDK.listTrackedFiles(configurationId, 1, 100);
  const allItems = [...firstResponse.items];
  if (firstResponse.total && firstResponse.total > 100) {
    const totalPages = firstResponse.pages || Math.ceil(firstResponse.total / 100);
    const pagePromises = [];
    for (let page = 2; page <= totalPages; page++) {
      pagePromises.push(clientSDK.listTrackedFiles(configurationId, page, 100));
    }
    const remainingPages = await Promise.all(pagePromises);
    remainingPages.forEach((pageResponse) => {
      allItems.push(...pageResponse.items);
    });
  }
  return allItems.map((item) => ({
    id: item.id,
    fileId: item.fileId,
    currentFileRevisionId: item.currentFileRevisionId,
    pinnedFileRevisionId: item.pinnedFileRevisionId,
    specifierType: item.specifierType,
    archiveStatus: item.archiveStatus,
    resourceId: item.resourceId,
    path: item.path,
  }));
}

const EMPTY_FOLDERS: readonly string[] = Object.freeze([]);

/**
 * The config's persisted empty folders — the ones holding no tracked file, which ride on the
 * configuration itself rather than on any file's path. Encoded exactly as `buildFolderPath` emits,
 * so the directory tree and the folder session consume them without re-encoding.
 */
export const useConfigurationEmptyFolders = (configurationId: string | undefined): readonly string[] => {
  const query = useQuery({
    queryKey: systemsQueryKeys.configuration(configurationId!),
    queryFn: () => clientSDK.getConfiguration(configurationId!),
    enabled: !!configurationId,
    staleTime: Infinity,
    select: (configuration) => configuration.emptyFolders ?? EMPTY_FOLDERS,
  });
  return query.data ?? EMPTY_FOLDERS;
};
